import { ReactNode, useId, useMemo, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

import { DropdownEmptyState } from '@/components/DropdownEmptyState';
import { DropdownSearch } from '@/components/DropdownSearch';
import { FieldClearButton } from '@/components/FieldClearButton';
import { InputLabel } from '@/components/InputLabel';
import { Button } from '@/components/ui/button';
import {
  FIELD_PLACEHOLDER_TEXT_CLASS_NAMES,
  type FieldSize,
  inputVariants,
} from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  dropdownItemVariants,
  Select as SelectUI,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { cn } from '@/lib/utils';

type SelectOption = {
  id: string | number;
  name: string;
  icon?: ReactNode;
  /**
   * Optional group heading (pass it already translated). Adjacent options with
   * the same group render under one label; options without a group stay flat.
   */
  group?: string;
  isDisabled?: boolean;
};

type OptionGroup = {
  label: string | null;
  options: SelectOption[];
};

// A flat list (no groups) yields a single unlabeled group — rendering is unchanged.
const groupOptions = (options: SelectOption[]): OptionGroup[] => {
  const groups: OptionGroup[] = [];
  options.forEach((option) => {
    const label = option.group ?? null;
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.label === label) {
      lastGroup.options.push(option);
    } else {
      groups.push({ label, options: [option] });
    }
  });
  return groups;
};

type SelectProps = {
  value?: string;
  onValueChange?: (value: string) => void;
  onClear?: () => void;
  options: SelectOption[];
  label?: ReactNode;
  hint?: ReactNode;
  isRequired?: boolean;
  icon?: ReactNode;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  className?: string;
  id?: string;
  'aria-invalid'?: boolean;
  'aria-label'?: string;
  isInvalid?: boolean;
  size?: FieldSize;
};

const Select = (props: SelectProps) => {
  const {
    value,
    onValueChange,
    onClear,
    options,
    label,
    hint,
    isRequired,
    icon,
    placeholder,
    searchPlaceholder,
    disabled,
    searchable = false,
    className,
    id,
    size = 'md',
    'aria-invalid': ariaInvalid,
    'aria-label': ariaLabel,
    isInvalid,
  } = props;

  const generatedId = useId();
  const selectId = id ?? generatedId;

  const [isFocused, setIsFocused] = useState(false);
  const [search, setSearch] = useState('');

  const selectedOption = useMemo(
    () => options.find((opt) => String(opt.id) === value),
    [options, value],
  );

  // Leading nbsp marks hierarchy depth in the dropdown rows — the trigger
  // shows the bare name.
  const selectedName = selectedOption?.name.replace(/^\u00A0+/u, '');

  const renderTriggerValue = (): ReactNode => {
    if (!selectedOption) {
      return (
        <span
          className={cn(
            'text-muted-foreground',
            FIELD_PLACEHOLDER_TEXT_CLASS_NAMES[size],
          )}
        >
          {placeholder}
        </span>
      );
    }
    if (!selectedOption.icon) {
      return selectedName;
    }
    return (
      <span className="flex items-center gap-2">
        <span className="flex shrink-0 items-center">
          {selectedOption.icon}
        </span>
        {selectedName}
      </span>
    );
  };

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const lowerSearch = search.toLowerCase();
    return options.filter((option) =>
      option.name.toLowerCase().includes(lowerSearch),
    );
  }, [options, search]);

  const isInvalidState = Boolean(ariaInvalid || isInvalid);

  const clearButton = onClear && selectedOption && !disabled && (
    <FieldClearButton onClear={onClear} />
  );

  const labelNode = label && (
    <InputLabel
      htmlFor={selectId}
      label={label}
      hint={hint}
      isRequired={isRequired}
      isInvalid={isInvalidState}
    />
  );

  if (searchable) {
    return (
      <div className="flex flex-col gap-2">
        {labelNode}
        <Popover
          modal
          open={isFocused}
          onOpenChange={setIsFocused}
        >
          <PopoverTrigger asChild>
            <Button
              id={selectId}
              type="button"
              variant="field"
              role="combobox"
              aria-expanded={isFocused}
              aria-invalid={ariaInvalid}
              aria-label={ariaLabel}
              disabled={disabled}
              data-state={isFocused ? 'open' : 'closed'}
              className={cn(
                'group/trigger justify-between',
                inputVariants({ kind: 'trigger', size }),
                className,
              )}
            >
              {icon && (
                <span className="text-muted-foreground flex shrink-0 items-center [&>svg]:size-5">
                  {icon}
                </span>
              )}
              <span
                className={cn(
                  'flex-1 truncate text-left',
                  !selectedName && [
                    'text-muted-foreground',
                    FIELD_PLACEHOLDER_TEXT_CLASS_NAMES[size],
                  ],
                )}
              >
                {selectedName || placeholder || '\u00A0'}
              </span>
              {clearButton}
              <ChevronDown className="ml-2 size-4 shrink-0 opacity-50 transition-transform duration-200 group-data-[state=open]/trigger:rotate-180" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            variant="dropdown"
            className="w-(--radix-popover-trigger-width)"
            align="start"
            onCloseAutoFocus={() => setSearch('')}
          >
            <DropdownSearch
              value={search}
              placeholder={
                searchPlaceholder ||
                (typeof label === 'string' ? `${label}...` : 'Search...')
              }
              onChange={setSearch}
            />
            <div className="mt-1 flex max-h-75 flex-col gap-1 overflow-y-auto p-0">
              {filteredOptions.length === 0 ? (
                <DropdownEmptyState isSearchResult={options.length > 0} />
              ) : (
                groupOptions(filteredOptions).flatMap((group, groupIndex) => [
                  group.label ? (
                    <div
                      key={`label-${group.label}-${groupIndex}`}
                      className="text-muted-foreground px-2 py-1.5 text-xs font-medium"
                    >
                      {group.label}
                    </div>
                  ) : null,
                  ...group.options.map((option) => {
                    const optionIdString = String(option.id);
                    const isSelected = value === optionIdString;
                    const { isDisabled } = option;
                    return (
                      <div
                        key={optionIdString}
                        role="option"
                        aria-selected={isSelected}
                        aria-disabled={isDisabled}
                        className={cn(
                          "[&_svg:not([class*='text-'])]:text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground flex shrink-0 cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none",
                          dropdownItemVariants({ isSelected, size }),
                          isDisabled && 'pointer-events-none opacity-50',
                        )}
                        onClick={() => {
                          onValueChange?.(optionIdString);
                          setIsFocused(false);
                        }}
                      >
                        <Check
                          className={cn(
                            'hidden size-4 shrink-0',
                            isSelected ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        {option.icon && (
                          <span className="flex shrink-0 items-center">
                            {option.icon}
                          </span>
                        )}
                        <span className="flex-1 text-sm">{option.name}</span>
                      </div>
                    );
                  }),
                ])
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-2">
      {labelNode}
      <SelectUI
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
        onOpenChange={(open) => setIsFocused(open)}
      >
        <SelectTrigger
          id={selectId}
          aria-label={ariaLabel}
          className={cn(
            'focus-visible:border-primary data-[state=open]:border-primary aria-invalid:border-destructive h-10! w-full border-2 ring-0 outline-none hover:cursor-pointer focus-visible:ring-0 aria-invalid:ring-0 *:data-[slot=select-value]:min-w-0 *:data-[slot=select-value]:flex-1 *:data-[slot=select-value]:overflow-hidden *:data-[slot=select-value]:text-ellipsis',
            inputVariants({ kind: 'trigger', size, height: 'forced' }),
            className,
          )}
          aria-invalid={ariaInvalid}
        >
          {/* Children must always be defined: with no children Radix portals the
              selected ItemText into this node, and flipping between the two
              modes crashes ("ref ... as a container to createPortal"). */}
          {icon && (
            <span className="text-muted-foreground flex shrink-0 items-center [&>svg]:size-5">
              {icon}
            </span>
          )}
          <SelectValue placeholder={placeholder}>
            {renderTriggerValue()}
          </SelectValue>
          {clearButton}
        </SelectTrigger>
        <SelectContent>
          {options.length === 0 ? (
            <DropdownEmptyState />
          ) : (
            groupOptions(options).map((group, groupIndex) => (
              <SelectGroup key={group.label ?? `group-${groupIndex}`}>
                {group.label && <SelectLabel>{group.label}</SelectLabel>}
                {group.options.map((option) => (
                  <SelectItem
                    size={size}
                    key={String(option.id)}
                    value={String(option.id)}
                    disabled={option.isDisabled}
                  >
                    {option.icon ? (
                      <span className="flex items-center gap-2">
                        <span className="flex shrink-0 items-center">
                          {option.icon}
                        </span>
                        {option.name}
                      </span>
                    ) : (
                      option.name
                    )}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))
          )}
        </SelectContent>
      </SelectUI>
    </div>
  );
};

export { Select };
export type { SelectOption, SelectProps };
